// src/rms/pages/Customers.js
// ============================================================================
// Customer records
// ============================================================================
// Called "Customers", "Tenants" or "Accounts" depending on the vertical. ID
// verification is captured here because several verticals hard-block checkout
// until it exists.
// ============================================================================

import React, { useMemo, useState } from "react";
import db from "../data";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import { isLocalBackend } from "../data";
import {
  uploadScan,
  signedUrlFor,
  removeScan,
  removeScans,
  humanSize,
  ACCEPT_ATTRIBUTE,
  MAX_BYTES,
} from "../data/documentStorage";
import { PageHeader, StatusPill, Modal, Field, TextInput, Select, Banner, EmptyState, formatDate, DemoPill } from "../components/ui";
import adEngine from "../ads/adEngine";
import { getLocale } from "../i18n/locale";

/** Today, for capping a date that cannot be in the future. Local, not UTC. */
const TODAY = (() => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
})();

function Customers() {
  const { operator, config, t, tLower, workflow, invalidate, formatMoney } = useRms();
  const { rows: customers } = useCollection("customers", {}, { orderBy: "last_name" });
  const { rows: customerDocuments } = useCollection("customerDocuments");
  const { rows: reservations } = useCollection("reservations");
  const { rows: invoices } = useCollection("invoices");

  // Which documents this trade takes, and which it insists on. Comes from the
  // vertical preset, so a boat operator is offered a boating licence and a
  // construction yard an operator certification card.
  const documentConfig = useMemo(() => {
    const docs = config?.operations?.documents;
    return { accepted: docs?.accepted || [], required: docs?.required || [] };
  }, [config]);

  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const docsByCustomer = useMemo(() => {
    const map = {};
    customerDocuments.forEach((d) => {
      (map[d.customer_id] = map[d.customer_id] || []).push(d);
    });
    return map;
  }, [customerDocuments]);

  const stats = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      const rentals = reservations.filter((r) => r.customer_id === c.id).length;
      const owed = invoices
        .filter((i) => i.customer_id === c.id && ["sent", "partially_paid", "overdue"].includes(i.status))
        .reduce((sum, i) => sum + (i.total_cents - i.amount_paid_cents), 0);
      map[c.id] = { rentals, owed };
    });
    return map;
  }, [customers, reservations, invoices]);

  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  const incompleteCount = useMemo(
    () => customers.filter((c) => c.is_provisional).length,
    [customers]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = onlyIncomplete ? customers.filter((c) => c.is_provisional) : customers;
    if (!q) return base;
    return base.filter((c) =>
      `${c.first_name} ${c.last_name} ${c.email} ${c.phone} ${c.company_name || ""}`.toLowerCase().includes(q)
    );
  }, [customers, search, onlyIncomplete]);

  const handleSave = async (draft) => {
    adEngine.recordAction();
    const payload = {
      operator_id: operator.id,
      first_name: draft.first_name,
      last_name: draft.last_name || null,
      email: draft.email || null,
      phone: draft.phone || null,
      company_name: draft.company_name || null,
      city: draft.city || null,
      region: draft.region || null,
      // A provisional record stops being provisional the moment somebody gives
      // it a way to be contacted. Cleared here rather than by a separate
      // "mark complete" action, because an operator who has just typed in a
      // phone number should not also have to tell us they did.
      is_provisional: !(String(draft.email || "").trim() || String(draft.phone || "").trim()),
      is_blocked: draft.is_blocked,
      blocked_reason: draft.is_blocked ? draft.blocked_reason || "Blocked by staff" : null,
      id_verification: draft.verified
        ? {
            verified_at: draft.verified_at || new Date().toISOString(),
            method: draft.id_method || "drivers_license",
            license_number: draft.license_number || null,
            license_expiry: draft.license_expiry || null,
            insurance_on_file: !!draft.insurance_on_file,
          }
        : {},
    };

    const saved = draft.id
      ? (await db.update("customers", draft.id, payload)).data
      : (await db.create("customers", payload)).data;

    // Documents are staged in the draft so that cancelling the dialog cancels
    // them too, then reconciled here against what is already stored.
    const customerId = saved?.id || draft.id;
    if (customerId) {
      for (const doc of draft.documents || []) {
        if (doc.deleted) {
          if (doc.id) await db.remove("customerDocuments", doc.id);
          // The scan goes with the row. Leaving the image behind would mean a
          // deleted licence photo still sitting in the bucket.
          if (doc.storage_path) await removeScan(doc.storage_path);
          continue;
        }

        const row = {
          operator_id: operator.id,
          customer_id: customerId,
          doc_type: doc.doc_type,
          label: doc.label || null,
          document_number: doc.document_number || null,
          issuer: doc.issuer || null,
          issuing_region: doc.issuing_region || null,
          issued_on: doc.issued_on || null,
          expires_on: doc.expires_on || null,
          coverage_amount_cents:
            doc.coverage_amount_cents === "" || doc.coverage_amount_cents === null
              ? null
              : Number(doc.coverage_amount_cents),
          coverage_currency: doc.coverage_amount_cents ? operator.currency : null,
          policy_holder: doc.policy_holder || null,
          status: doc.status || "pending",
          verified_at: doc.status === "verified" ? doc.verified_at || new Date().toISOString() : null,
          notes: doc.notes || null,

          // The scan. Easy to omit from a hand-built row like this one, and the
          // failure is silent: the upload succeeds, the file sits in the
          // bucket, and the document shows no attachment.
          storage_path: doc.storage_path || null,
          file_name: doc.file_name || null,
          mime_type: doc.mime_type || null,
          size_bytes: doc.size_bytes ?? null,
        };

        if (doc.id) await db.update("customerDocuments", doc.id, row);
        else await db.create("customerDocuments", row);
      }
    }

    setEditing(null);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("customerPlural", "Customers")}
        subtitle={workflow.idVerificationRequired
            ? t("customers.subtitleVerified", "{count} on file · {verified} ID-verified", {
                count: customers.length,
                verified: customers.filter((c) => c.id_verification?.verified_at).length,
              })
            : t("customers.subtitle", "{count} on file", { count: customers.length })}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankCustomer())}>
            {t("customers.add", "Add {customer}", { customer: tLower("customer", "customer") })}
          </button>
        }
      />

      {workflow.idVerificationRequired && customers.some((c) => !c.id_verification?.verified_at) ? (
        <Banner tone="warn">
          {t("customers.unverifiedWarning", "Some {people} have no verified ID. This business", {
            people: tLower("customerPlural", "customers"),
          })}
          type blocks checkout until ID is verified.
        </Banner>
      ) : null}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <input
          className="rms-input"
          style={{ maxWidth: 300 }}
          placeholder={t("customers.search", "Search {people}…", { people: tLower("customerPlural", "customers") })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Offered only when there is something to filter to. A mark nobody
            can act on in bulk is decoration; a filter for an empty set is
            worse, because it reads as a feature that is broken. */}
        {incompleteCount ? (
          <button
            type="button"
            className={`rms-btn ${onlyIncomplete ? "" : "rms-btn--ghost"} rms-btn--sm`}
            onClick={() => setOnlyIncomplete((v) => !v)}
          >
            {t("customers.incompleteCount", "{count, plural, one {# incomplete} other {# incomplete}}", {
              count: incompleteCount,
            })}
          </button>
        ) : null}
      </div>

      {customers.length === 0 ? (
        <EmptyState
          title={t("customers.empty.title", "No {people} yet", { people: tLower("customerPlural", "customers") })}
          message={t("customers.addSomeoneToRent", "Add someone to rent to.")}
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => setEditing(blankCustomer())}>
              {t("customers.add", "Add {customer}", { customer: tLower("customer", "customer") })}
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("tax.rates.name", "Name")}</th>
                <th>{t("customers.contact", "Contact")}</th>
                <th>ID</th>
                <th className="rms-num">{t("customers.rentals", "Rentals")}</th>
                <th className="rms-num">{t("customers.owing", "Owing")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{`${c.first_name} ${c.last_name || ""}`.trim()}</strong>
                    {c.is_demo ? <> <DemoPill /></> : null}
                    {c.is_provisional ? (
                      <>
                        {" "}
                        <span
                          className="rms-pill"
                          title={t("customers.incompleteHint", "Created from a name at the counter. Add contact details to finish it.")}
                        >
                          {t("customers.incomplete", "Incomplete")}
                        </span>
                      </>
                    ) : null}
                    {c.company_name ? <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>{c.company_name}</div> : null}
                    {c.is_blocked ? (
                      <div style={{ marginTop: 4 }}>
                        <StatusPill status="blocked" label="blocked" />
                      </div>
                    ) : null}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {c.email || "—"}
                    <div style={{ color: "var(--rms-muted)" }}>{c.phone || ""}</div>
                  </td>
                  <td>
                    <DocumentSummary
                      documents={docsByCustomer[c.id] || []}
                      accepted={documentConfig.accepted}
                      required={documentConfig.required}
                      legacyVerifiedAt={c.id_verification?.verified_at}
                    />
                  </td>
                  <td className="rms-num">{stats[c.id]?.rentals ?? 0}</td>
                  <td className="rms-num" style={{ color: stats[c.id]?.owed ? "var(--rms-danger)" : undefined }}>
                    {stats[c.id]?.owed ? formatMoney(stats[c.id].owed) : "—"}
                  </td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setEditing(toDraft(c, customerDocuments))}>
                      {t("action.edit", "Edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <CustomerForm
          draft={editing}
          documentConfig={documentConfig}
          operatorId={operator.id}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      ) : null}
    </>
  );
}

function CustomerForm({ draft: initial, documentConfig, operatorId, onCancel, onSave }) {
  const { t, tLower } = useRms();
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const acceptedDocs = documentConfig.accepted;
  const requiredDocs = documentConfig.required;

  // Scans upload the moment they are chosen, so abandoning the dialog would
  // leave them in the bucket referenced by nothing. Every path is recorded here
  // and swept on cancel.
  const uploadedPaths = React.useRef([]);

  const cancel = async () => {
    await removeScans(uploadedPaths.current);
    uploadedPaths.current = [];
    onCancel();
  };

  const submit = () => {
    if (!draft.first_name.trim()) {
      return setError(t("customers.error.firstName", "A first name is required."));
    }

    // A missing document does NOT stop the save, and used to.
    //
    // Three things were wrong with blocking here, and they compound:
    //
    //   * 20260820180000 added provisional customers precisely so one could be
    //     "created from a name alone at the counter or on the phone", with
    //     "checkout is where identity is enforced". The schema supports a
    //     name-only customer; this form refused to save one.
    //   * It applied to EDITS too. Every customer seeded at provisioning
    //     arrives with no documents, so on the seven verticals that require one
    //     none of them could be edited at all -- not even to correct a typo in
    //     an email address. Found exactly that way.
    //   * The checkbox below offers "a quick sign-off when nobody needs the
    //     paperwork recorded", which this made impossible.
    //
    // What actually guards a handover is unchanged and lives in
    // rentalService.checkoutBlockers: an unverified customer cannot be handed a
    // vehicle on a vertical that requires ID. Nothing is loosened by this --
    // the counter gate never depended on this check.
    //
    // The missing document is still stated, permanently, in the Documents
    // section above. It is a fact about the customer, not a reason to refuse to
    // write their phone number down.
    setError(null);
    onSave(draft);
  };

  return (
    <Modal
      title={draft.id ? t("customers.edit", "Edit {customer}", { customer: tLower("customer", "customer") }) : t("customers.form.new", "New {customer}", { customer: tLower("customer", "customer") })}
      onClose={cancel}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={cancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit}>
            {t("action.save", "Save")}
          </button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="rms-field-grid">
        <Field label={t("customers.firstName", "First name")} required>
          <TextInput value={draft.first_name} onChange={(v) => set("first_name", v)} />
        </Field>
        <Field label={t("customers.lastName", "Last name")}>
          <TextInput value={draft.last_name} onChange={(v) => set("last_name", v)} />
        </Field>
        <Field label={t("customers.email", "Email")}>
          <TextInput type="email" value={draft.email} onChange={(v) => set("email", v)} />
        </Field>
        <Field label={t("customers.phone", "Phone")}>
          <TextInput value={draft.phone} onChange={(v) => set("phone", v)} />
        </Field>
        <Field label={t("customers.company", "Company")}>
          <TextInput value={draft.company_name} onChange={(v) => set("company_name", v)} />
        </Field>
        <Field label={t("customers.city", "City")}>
          <TextInput value={draft.city} onChange={(v) => set("city", v)} />
        </Field>
      </div>

      <DocumentsSection
        documents={draft.documents}
        onChange={(next) => set("documents", next)}
        required={requiredDocs}
        accepted={acceptedDocs}
        operatorId={operatorId}
        customerId={draft.id}
        onUploaded={(path) => uploadedPaths.current.push(path)}
      />

      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 4 }}>
        <label className="rms-checkline">
          <input type="checkbox" checked={!!draft.verified} onChange={(e) => set("verified", e.target.checked)} />
          {t("customers.identityCheckedAtThe", "Identity checked at the counter")}
        </label>
        <p className="rms-help" style={{ margin: "2px 0 0 24px" }}>
          {t("customers.aQuickSignOff", "A quick sign-off when nobody needs the paperwork recorded. Anything you actually have to produce later — a licence, a certificate of insurance — belongs above, where it gets a type and an expiry date.")}
        </p>
      </div>

      <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 10 }}>
        <label className="rms-checkline">
          <input type="checkbox" checked={!!draft.is_blocked} onChange={(e) => set("is_blocked", e.target.checked)} />
          {t("customers.blockLabel", "Block this {customer} from renting", {
            customer: tLower("customer", "customer"),
          })}
        </label>
        {draft.is_blocked ? (
          <Field label={t("customers.reason", "Reason")}>
            <TextInput value={draft.blocked_reason} onChange={(v) => set("blocked_reason", v)} />
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Documents
// ----------------------------------------------------------------------------

const labelForDoc = (accepted, key) =>
  accepted.find((d) => d.key === key)?.label || key.replace(/_/g, " ");

/** Past its expiry date. A document with no expiry never expires. */
const isExpired = (doc) => !!doc.expires_on && doc.expires_on < new Date().toISOString().slice(0, 10);

/** Verified, and not lapsed. Both conditions, always — see the pill below. */
const isValid = (doc) => doc.status === "verified" && !isExpired(doc);

/**
 * The paperwork state of one customer, at a glance in the list.
 *
 * "Expiring soon" is 30 days, which is roughly the notice an operator needs to
 * chase a renewal before it becomes a refused rental at the counter.
 */
function DocumentSummary({ documents, accepted, required, legacyVerifiedAt }) {
  const { t } = useRms();
  const expired = documents.filter(isExpired);
  const missing = required.filter((key) => !documents.some((d) => d.doc_type === key && isValid(d)));

  const soonCutoff = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const expiringSoon = documents.filter(
    (d) => d.expires_on && !isExpired(d) && d.expires_on <= soonCutoff
  );

  if (missing.length) {
    return (
      <>
        <StatusPill status="overdue" label={expired.length ? "expired" : "missing"} />
        <div style={{ fontSize: 11.5, color: "var(--rms-muted)", marginTop: 3 }}>
          {missing.map((k) => labelForDoc(accepted, k)).join(", ")}
        </div>
      </>
    );
  }

  if (!documents.length) {
    // Pre-dates the documents table. Honest about being a bare sign-off rather
    // than showing the same green pill as a recorded, in-date licence.
    return legacyVerifiedAt ? (
      <>
        <StatusPill status="pending" label="checked" />
        <div style={{ fontSize: 11.5, color: "var(--rms-muted)", marginTop: 3 }}>
          no documents on file
        </div>
      </>
    ) : (
      <StatusPill status="pending" label="none on file" />
    );
  }

  return (
    <>
      <StatusPill status="verified" label={t("customers.docsOnFile", "{count} on file", { count: documents.filter(isValid).length })} />
      {expiringSoon.length ? (
        <div style={{ fontSize: 11.5, color: "var(--rms-warn)", marginTop: 3 }}>
          {labelForDoc(accepted, expiringSoon[0].doc_type)} expires {formatDate(expiringSoon[0].expires_on)}
        </div>
      ) : null}
    </>
  );
}

const blankDocument = (docType) => ({
  id: null,
  doc_type: docType,
  label: "",
  document_number: "",
  issuer: "",
  issuing_region: "",
  issued_on: "",
  expires_on: "",
  coverage_amount_cents: "",
  policy_holder: "",
  status: "pending",
  notes: "",
});

/**
 * Paperwork on file for one customer.
 *
 * The list of types comes from the vertical preset, so a boat operator is
 * offered a boating licence and a construction yard is offered an operator
 * certification card — neither has to scroll past the other's paperwork.
 *
 * Rows are staged in the draft and written when the customer is saved, so
 * cancelling the dialog cancels the documents too.
 */
function DocumentsSection({ documents = [], onChange, required = [], accepted = [], operatorId, customerId, onUploaded }) {
  const { t } = useRms();
  const [adding, setAdding] = useState(null);

  const live = documents.filter((d) => !d.deleted);

  const add = (doc) => {
    onChange([...documents, doc]);
    setAdding(null);
  };

  const removeAt = (index) => {
    // Existing rows are tombstoned so the save can delete them server-side;
    // rows never saved are dropped outright.
    const next = documents.map((d, i) => (i === index ? { ...d, deleted: true } : d));
    onChange(next.filter((d) => d.id || !d.deleted));
  };

  return (
    <div style={{ borderTop: "1px solid var(--rms-border)", paddingTop: 14, marginTop: 4 }}>
      <h3 className="rms-section-title" style={{ marginBottom: 4 }}>{t("customers.documents", "Documents")}</h3>
      <p className="rms-help" style={{ marginTop: 0 }}>
        {t("customers.licencesCertificatesOfInsurance", "Licences, certificates of insurance and anything else you need on file. Each one carries its own expiry, so a policy that lapsed shows as lapsed instead of quietly still counting.")}
      </p>

      {required.length ? (
        <p style={{ fontSize: 12.5, color: "var(--rms-muted-strong)", margin: "6px 0 0" }}>
          {/* "Usually needed", not "required": what actually stops a handover is
              the identity check, which the sign-off below can satisfy on its
              own. Saying "required" here promised a rule this screen does not
              enforce and checkout does not apply. */}
          {t("customers.usuallyNeeded", "Usually needed before checkout:")}{" "}
          {required.map((k) => (
            <span key={k} className="rms-pill rms-pill--info" style={{ marginInlineEnd: 4 }}>
              {labelForDoc(accepted, k)}
            </span>
          ))}
        </p>
      ) : null}

      {live.length ? (
        <div className="rms-table-wrap" style={{ marginTop: 12 }}>
          <table className="rms-table" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>{t("customers.document", "Document")}</th>
                <th>{t("certifications.number", "Number")}</th>
                <th>{t("customers.issuer", "Issuer")}</th>
                <th>{t("certifications.expires", "Expires")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, index) => {
                if (doc.deleted) return null;
                const expired = isExpired(doc);
                return (
                  <tr key={doc.id || `new-${index}`}>
                    <td>
                      <strong>
                        {doc.doc_type === "other" && doc.label
                          ? doc.label
                          : labelForDoc(accepted, doc.doc_type)}
                      </strong>
                      {doc.coverage_amount_cents ? (
                        <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                          {`Cover ${(Number(doc.coverage_amount_cents) / 100).toLocaleString(getLocale(), { style: "currency", currency: "USD", maximumFractionDigits: 0 })}`}
                          {doc.policy_holder ? ` · ${doc.policy_holder}` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 12.5, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                      {doc.document_number || "—"}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{doc.issuer || "—"}</td>
                    <td style={{ fontSize: 12.5, color: expired ? "var(--rms-danger)" : undefined }}>
                      {doc.expires_on ? formatDate(doc.expires_on) : "—"}
                    </td>
                    <td>
                      {/* Expiry beats verification. A verified certificate that
                          ran out last month must never read as good. */}
                      {expired ? (
                        <StatusPill status="overdue" label="expired" />
                      ) : (
                        <StatusPill status={doc.status === "verified" ? "verified" : "pending"} label={doc.status} />
                      )}
                    </td>
                    <td className="rms-num">
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => removeAt(index)}>
                        {t("campaignForm.remove", "Remove")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "var(--rms-muted)", margin: "10px 0 0" }}>
          {t("customers.nothingOnFileYet", "Nothing on file yet.")}
        </p>
      )}

      {adding ? (
        <DocumentEditor
          doc={adding}
          accepted={accepted}
          operatorId={operatorId}
          customerId={customerId}
          onUploaded={onUploaded}
          onChange={setAdding}
          onSave={() => add(adding)}
          onCancel={async () => {
            // Abandoning the form must not strand the scan it uploaded.
            if (adding.storage_path) await removeScan(adding.storage_path);
            setAdding(null);
          }}
        />
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <Select
            value=""
            onChange={(v) => v && setAdding(blankDocument(v))}
            placeholder="Add a document…"
            options={accepted.map((d) => ({ value: d.key, label: d.label }))}
          />
        </div>
      )}
    </div>
  );
}

/** The add form. Shows insurance fields only when the type actually is one. */
function DocumentEditor({ doc, accepted, onChange, onSave, onCancel, operatorId, customerId, onUploaded }) {
  const { t } = useRms();
  const spec = accepted.find((d) => d.key === doc.doc_type) || {};
  const set = (k, v) => onChange({ ...doc, [k]: v });

  return (
    <div
      style={{
        marginTop: 12, padding: 14,
        border: "1px solid var(--rms-border)", borderRadius: 10,
        background: "var(--rms-subtle)",
      }}
    >
      <h4 style={{ fontSize: 13, margin: "0 0 10px" }}>
        {doc.doc_type === "other" ? t("customers.otherDocument", "Other document") : spec.label}
      </h4>

      <div className="rms-field-grid">
        {doc.doc_type === "other" ? (
          <Field label={t("customers.whatIsIt", "What is it?")} required help={t("customers.yourOwnWordingThis", "Your own wording — this is what staff will see.")}>
            <TextInput value={doc.label} onChange={(v) => set("label", v)} placeholder="e.g. Site induction certificate" />
          </Field>
        ) : null}

        <Field label={spec.insurance ? t("customers.policyNumber", "Policy number") : t("customers.documentNumber", "Document number")}>
          <TextInput value={doc.document_number} onChange={(v) => set("document_number", v)} />
        </Field>

        <Field label={spec.insurance ? t("customers.insuranceCarrier", "Insurance carrier") : t("customers.issuedBy", "Issued by")}>
          <TextInput
            value={doc.issuer}
            onChange={(v) => set("issuer", v)}
            placeholder={spec.insurance ? "e.g. Aviva" : "e.g. Ontario MTO"}
          />
        </Field>

        {spec.region ? (
          <Field label={t("customers.issuingStateProvince", "Issuing state / province")} help={t("customers.validityRulesDifferBy", "Validity rules differ by jurisdiction.")}>
            <TextInput value={doc.issuing_region} onChange={(v) => set("issuing_region", v)} />
          </Field>
        ) : null}

        {spec.insurance ? (
          <>
            <Field label={t("customers.policyHolder", "Policy holder")} help={t("customers.leaveBlankIfIt", "Leave blank if it's the customer themselves.")}>
              <TextInput value={doc.policy_holder} onChange={(v) => set("policy_holder", v)} placeholder="e.g. Northgate Construction Ltd" />
            </Field>
            <Field label={t("customers.coverageAmount", "Coverage amount")} help={t("customers.liabilityLimitInWhole", "Liability limit, in whole units of currency.")}>
              <TextInput
                type="number"
                value={doc.coverage_amount_cents === "" ? "" : Number(doc.coverage_amount_cents) / 100}
                onChange={(v) => set("coverage_amount_cents", v === "" ? "" : Math.round(Number(v) * 100))}
                placeholder="1000000"
              />
            </Field>
          </>
        ) : null}

        {/* max, not min. A licence was issued in the past -- often years back --
            and the picker opening on this month made that look like the earliest
            date allowed. Capping the future end says which direction the field
            runs, stops a typo putting an issue date next year, and leaves the
            year selector free to go back as far as anybody needs. */}
        <Field
          label={t("customers.issuedOn", "Issued on")}
          help={t("customers.issuedOnHelp", "The date on the document. Type it if it is years back.")}
        >
          <input
            className="rms-input"
            type="date"
            value={doc.issued_on || ""}
            max={TODAY}
            onChange={(e) => set("issued_on", e.target.value)}
          />
        </Field>

        {spec.expires === false ? null : (
          <Field label={t("customers.expires", "Expires")} help={t("customers.leaveBlankIfIt2", "Leave blank if it does not expire.")}>
            {/* No max: an expired licence is a real thing to record, and
                refusing to write one down would hide exactly the document
                somebody needs to be warned about. Only the ordering is
                enforced. */}
            <input
              className="rms-input"
              type="date"
              value={doc.expires_on || ""}
              min={doc.issued_on || undefined}
              onChange={(e) => set("expires_on", e.target.value)}
            />
          </Field>
        )}
      </div>

      <label className="rms-checkline">
        <input
          type="checkbox"
          checked={doc.status === "verified"}
          onChange={(e) => set("status", e.target.checked ? "verified" : "pending")}
        />
        {t("customers.originalSeenAndChecked", "Original seen and checked")}
      </label>

      <ScanPicker
        doc={doc}
        operatorId={operatorId}
        customerId={customerId}
        onAttached={(meta) => onChange({ ...doc, ...meta })}
        onUploaded={onUploaded}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button type="button" className="rms-btn rms-btn--primary rms-btn--sm" onClick={onSave}>
          {t("customers.addDocument", "Add document")}
        </button>
        <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={onCancel}>
          {t("action.cancel", "Cancel")}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

/**
 * Attaches a scan.
 *
 * The file uploads as soon as it is chosen rather than waiting for save, so a
 * file that is too large or the wrong type says so while the person still has
 * it in their hand. The cost is that a cancelled dialog could strand an object
 * in the bucket, so every path uploaded here is reported to the form, which
 * clears them if the dialog is abandoned. Orphaned licence photos are exactly
 * the kind of thing that should not accumulate.
 */
function ScanPicker({ doc, operatorId, customerId, onAttached, onUploaded }) {
  const { t } = useRms();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const inputRef = React.useRef(null);

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // let the same file be re-chosen after an error
    if (!file) return;

    setProblem(null);
    setBusy(true);

    const previous = doc.storage_path;
    const result = await uploadScan(file, { operatorId, customerId });

    setBusy(false);
    if (!result.success) return setProblem(result.error);

    onAttached({
      storage_path: result.data.storage_path,
      file_name: result.data.file_name,
      mime_type: result.data.mime_type,
      size_bytes: result.data.size_bytes,
    });
    onUploaded(result.data.storage_path);

    // Replacing a scan: the old object is no longer referenced by anything.
    if (previous && previous !== result.data.storage_path) await removeScan(previous);
  };

  const detach = async () => {
    if (doc.storage_path) await removeScan(doc.storage_path);
    onAttached({ storage_path: null, file_name: null, mime_type: null, size_bytes: null });
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed var(--rms-border)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          style={{ display: "none" }}
          onChange={choose}
        />
        <button
          type="button"
          className="rms-btn rms-btn--ghost rms-btn--sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? t("customers.uploading", "Uploading…") : doc.storage_path ? t("customers.replaceScan", "Replace scan") : t("customers.attachScan", "Attach a scan")}
        </button>

        {doc.storage_path ? (
          <>
            <span style={{ fontSize: 12.5, color: "var(--rms-muted-strong)" }}>
              {doc.file_name} · {humanSize(doc.size_bytes)}
            </span>
            <ViewScanLink path={doc.storage_path} />
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={detach}>
              {t("campaignForm.remove", "Remove")}
            </button>
          </>
        ) : (
          <span style={{ fontSize: 12.5, color: "var(--rms-muted)" }}>
            {t("customers.photoOrPdf", "Photo or PDF, up to {size}", { size: humanSize(MAX_BYTES) })}
          </span>
        )}
      </div>

      {problem ? <Banner tone="warn">{problem}</Banner> : null}

      <p className="rms-help" style={{ marginTop: 8 }}>
        {isLocalBackend
          ? "On the local backend the file is held for this browser session only — it disappears on reload. Switch to the Supabase backend to store scans properly."
          : "Stored privately. Only your staff can open it, through a link that expires after a minute."}
      </p>
    </div>
  );
}

/** Opens a scan through a signed URL minted at the moment of clicking. */
function ViewScanLink({ path }) {
  const { t } = useRms();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const open = async () => {
    setBusy(true);
    setProblem(null);
    const result = await signedUrlFor(path);
    setBusy(false);

    if (!result.success) return setProblem(result.error);
    // noopener: the scan opens in a tab that cannot reach back into this one.
    window.open(result.data, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button type="button" className="rms-linkish" onClick={open} disabled={busy}>
        {busy ? t("customers.opening", "Opening…") : t("customers.view", "View")}
      </button>
      {problem ? <Banner tone="warn">{problem}</Banner> : null}
    </>
  );
}

function blankCustomer() {
  return {
    id: null, first_name: "", last_name: "", email: "", phone: "", company_name: "",
    city: "", region: "", is_blocked: false, blocked_reason: "",
    verified: false, documents: [],
  };
}

function toDraft(c, documents = []) {
  return {
    id: c.id,
    first_name: c.first_name, last_name: c.last_name || "", email: c.email || "",
    phone: c.phone || "", company_name: c.company_name || "", city: c.city || "", region: c.region || "",
    is_blocked: !!c.is_blocked, blocked_reason: c.blocked_reason || "",
    verified: !!c.id_verification?.verified_at,
    verified_at: c.id_verification?.verified_at,

    documents: documents
      .filter((d) => d.customer_id === c.id)
      .map((d) => ({
        ...d,
        // Blank rather than null, so the inputs stay controlled.
        label: d.label || "",
        document_number: d.document_number || "",
        issuer: d.issuer || "",
        issuing_region: d.issuing_region || "",
        issued_on: d.issued_on || "",
        expires_on: d.expires_on || "",
        policy_holder: d.policy_holder || "",
        coverage_amount_cents: d.coverage_amount_cents ?? "",
      })),
  };
}

export default Customers;
